import React, { FunctionComponent, KeyboardEvent, useCallback } from 'react';
import styled from 'styled-components';
import THEME from 'theme';

interface CardProps {
  id: string;
  center?: boolean;
  column?: boolean;
  justify?: boolean;
  selected?: boolean;
  onClick?: () => void;
  role?: string;
  tabIndex?: number;
  ariaLive?: 'off' | 'assertive' | 'polite';
}

const Container = styled.div<CardProps>`
  display: flex;
  flex-direction: ${(props) => (props.column ? 'column' : 'row')};
  justify-content: ${(props) => (props.justify ? 'space-between' : 'none')};
  align-items: ${(props) => (props.center ? 'center' : 'left')};
  padding: 1rem;
  border-radius: 6px;
  border: ${(props) => (props.selected ? THEME.FIELD.BORDER.SELECTED : '1px')}
    solid
    ${(props) =>
      props.selected ? THEME.FIELD.BORDER.VALID : THEME.FIELD.BORDER.DEFAULT};
  cursor: ${(props) => (props.onClick === undefined ? 'default' : 'pointer')};

  div {
    p {
      margin: 0;
    }
  }

  .i-check-circle {
    color: ${(props) =>
      props.selected
        ? THEME.TICK_ICON.VALID
        : THEME.TICK_ICON.DEFAULT} !important;
  }
`;

export const Card: FunctionComponent<CardProps> = ({
  id,
  children,
  center,
  column,
  justify,
  selected,
  onClick,
  role,
  tabIndex = 0,
  ariaLive,
}) => {
  const handleKeyDown = useCallback(
    (evt: KeyboardEvent<HTMLDivElement>) => {
      if (!onClick) {
        return;
      }

      const code = evt.charCode || evt.keyCode;
      if (code === 32 || code === 13) {
        onClick();
      }
    },
    [onClick]
  );

  return (
    <Container
      role={role}
      tabIndex={tabIndex}
      id={id}
      aria-pressed={selected}
      center={center}
      column={column}
      justify={justify}
      selected={selected}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...(ariaLive && { 'aria-live': ariaLive })}
    >
      {children}
    </Container>
  );
};

export default Card;
